import React from "react";
import { typography, textColors, borders, surfaces, interactive } from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";

const ICON_SPACING = {
  "tracking-tighter": "-1px",
  "tracking-tight": "0px",
  "tracking-normal": "1px",
  "tracking-wide": "3px",
  "tracking-wider": "5px",
  "tracking-widest": "7px",
};

const ARROW_WIDTHS = {
  "tracking-tighter": 6,
  "tracking-tight": 9,
  "tracking-normal": 12,
  "tracking-wide": 16,
  "tracking-wider": 20,
  "tracking-widest": 24,
};

const READABLE_NAMES = {
  "tracking-tighter": "Tighter",
  "tracking-tight": "Tight",
  "tracking-normal": "Normal",
  "tracking-wide": "Wide",
  "tracking-wider": "Wider",
  "tracking-widest": "Widest",
};

function SpacingArrow({ width: arrowWidth, selected }) {
  const viewW = 24;
  const viewH = 5;
  const midX = viewW / 2;
  const midY = viewH / 2;
  const halfArrow = arrowWidth / 2;
  const leftX = midX - halfArrow;
  const rightX = midX + halfArrow;
  const tipSize = 1.5;
  const color = selected ? "white" : "currentColor";

  return (
    <svg
      viewBox={`0 0 ${viewW} ${viewH}`}
      width={viewW}
      height={viewH}
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <polyline
        points={`${leftX + tipSize},${midY - tipSize} ${leftX},${midY} ${leftX + tipSize},${midY + tipSize}`}
        fill="none"
        stroke={color}
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={leftX}
        y1={midY}
        x2={rightX}
        y2={midY}
        stroke={color}
        strokeWidth="1"
        strokeLinecap="round"
      />
      <polyline
        points={`${rightX - tipSize},${midY - tipSize} ${rightX},${midY} ${rightX - tipSize},${midY + tipSize}`}
        fill="none"
        stroke={color}
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpacingChip({ value, label, selected, onSelect }) {
  const spacingCss = ICON_SPACING[value] || "0px";
  const numericSpacing = parseFloat(spacingCss) || 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`w-9 h-10 flex flex-col items-center justify-center gap-0.5 border rounded-lg transition-all ${
        selected
          ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
          : `${borders.default} ${surfaces.tertiary} ${surfaces.hover} ${textColors.primary}`
      }`}
      aria-pressed={selected}
      title={`${READABLE_NAMES[value] || label} (${label})`}
    >
      <SpacingArrow width={ARROW_WIDTHS[value] || 12} selected={selected} />
      <span
        className={`text-[11px] font-semibold leading-none select-none ${selected ? "text-white" : textColors.primary}`}
        style={{
          letterSpacing: spacingCss,
          marginRight: `${-numericSpacing}px`,
        }}
      >
        AV
      </span>
    </button>
  );
}

export default function LetterSpacingField({ label, help, value, onChange, choices, infoText }) {
  const normalized = (choices || []).map((c) => ({ value: c.value, label: c.label }));

  return (
    <div className="field-wrapper">
      <div className="flex flex-wrap items-center gap-2">
        <label className={`mr-1 whitespace-nowrap ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-1.5"><InfoIcon text={infoText} /></span> : null}
        </label>
        <div className="flex flex-wrap items-center gap-1.5">
          {normalized.map((c) => (
            <SpacingChip
              key={c.value}
              value={c.value}
              label={c.label}
              selected={value === c.value}
              onSelect={(val) => onChange(val)}
            />
          ))}
        </div>
      </div>
      {help && <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
}
